import React, { useState, useMemo } from 'react';
import ListView from "../components/List/ProgramList";
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchPrograms } from "../store/ProgramSlice";
import debounce from 'lodash/debounce';

export default function Program() {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const dispatch = useDispatch<any>();

    const debouncedSearch = React.useMemo(
        () =>
            debounce((query: string) => {
                dispatch(fetchPrograms({ search: query, limit: 8 }));
            }, 300),
        [dispatch]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchTerm(query);
        debouncedSearch(query);
    };

    const handleClear = () => {
        setSearchTerm('');
        dispatch(fetchPrograms({ search: '', limit: 8 }));
    };

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Header Title */}
            <div className="flex items-center justify-between pb-4 shrink-0">
                <h2 className="text-xl sm:text-2xl font-bold font-gilroy text-[#1E293B] tracking-tight">
                    {t("adminPrograms.title", "Programs")}
                </h2>
            </div>

            {/* Main Content Card */}
            <div className="bg-white flex-1 min-h-[420px] rounded-2xl border border-gray-100 overflow-hidden flex flex-col shadow-xs">
                {/* Search & Action Bar */}
                <div className="flex flex-col sm:flex-row p-4 sm:p-5 pb-3 sm:pb-4 gap-3 sm:gap-4 items-center justify-between shrink-0 border-b border-gray-100 bg-white">
                    <form className="relative w-full sm:max-w-md" onSubmit={(e) => e.preventDefault()}>
                        <div className="relative flex items-center">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleChange}
                                placeholder={t("adminPrograms.searchPlaceholder", "Search programs by title...")}
                                className="w-full pl-10 pr-10 py-2.5 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm text-[#1E293B] placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#037897] focus:ring-2 focus:ring-[#037897]/15 transition-all"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </form>

                    <Link
                        to="add"
                        className="bg-[#B4804A] hover:bg-[#9d6f3e] active:scale-98 transition-all duration-200 flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl font-gilroy font-bold text-white text-sm whitespace-nowrap shadow-sm hover:shadow w-full sm:w-auto"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                        </svg>
                        <span>{t("adminPrograms.addNewProgram", "Add New Program")}</span>
                    </Link>
                </div>

                {/* Table Component */}
                <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                    <ListView searchTerm={searchTerm} />
                </div>
            </div>
        </div>
    );
}

